// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class HceDataCacheService {
  // ===== Configuration =====
  private static readonly CACHE_NAME = 'hce-data-cache';
  private static readonly MAX_BYTES = 100 * 1024 * 1024; // 100MB

  // ===== Cache Entry Headers =====
  private static readonly HDR_CREATED_AT = 'X-Created-At';
  private static readonly HDR_CONTENT_LENGTH = 'X-Content-Length';
  private static readonly HDR_ENCODING = 'Content-Encoding'; // gzip / identity
  private static readonly HDR_KEY = 'X-Key';

  // ===== Internal State =====
  private static warned = false;

  private get canGzip(): boolean {
    // Check if the browser supports the CompressionStream API (for gzip).
    return typeof (globalThis as any).CompressionStream !== 'undefined';
  }

  private ensureAvailable(): boolean {
    if (typeof window === 'undefined' || typeof caches === 'undefined') {
      if (!HceDataCacheService.warned) {
        console.warn(
          '[cache] Cache Storage API is not available (HTTP or unsupported environment)'
        );
        HceDataCacheService.warned = true;
      }
      return false;
    }
    return true;
  }

  private async open(): Promise<Cache> {
    // Open (or create) the named Cache storage.
    return caches.open(HceDataCacheService.CACHE_NAME);
  }

  /** Build a Request using the API URL as the cache key (GET only). */
  private makeRequest(url: string): Request {
    return new Request(url, { method: 'GET' });
  }

  private encode(text: string): Uint8Array {
    // Encode UTF-8 text into bytes.
    return new TextEncoder().encode(text);
  }

  private decode(buf: ArrayBuffer): string {
    // Decode bytes into a UTF-8 string.
    return new TextDecoder('utf-8').decode(buf);
  }

  private async gzip(bytes: Uint8Array): Promise<{ out: Uint8Array; enc: string }> {
    // Compress bytes with gzip when supported; otherwise store as-is (identity).
    if (!this.canGzip) {
      console.warn('[cache] CompressionStream not supported → storing as identity');
      return { out: bytes, enc: 'identity' };
    }
    const cs = new (globalThis as any).CompressionStream('gzip');
    const compressed = new Blob([bytes]).stream().pipeThrough(cs);
    const buf = await new Response(compressed).arrayBuffer();
    return { out: new Uint8Array(buf), enc: 'gzip' };
  }

  private async gunzip(buf: ArrayBuffer, enc: string): Promise<Uint8Array> {
    // Decompress only when the stored encoding is gzip; otherwise return raw bytes.
    if (enc !== 'gzip') return new Uint8Array(buf);

    const ds = new (globalThis as any).DecompressionStream('gzip');
    const decompressed = new Blob([buf]).stream().pipeThrough(ds);
    return new Uint8Array(await new Response(decompressed).arrayBuffer());
  }

  /** Persist a text payload into Cache storage (gzip when available). */
  async put(apiUrl: string, text: string): Promise<void> {
    if (!this.ensureAvailable()) return;
    if (text == null || text === '') return;
    const cache = await this.open();

    const raw = this.encode(text);
    if (raw.byteLength === 0) {
      console.info('[cache] Skip put: encoded length is 0 for', apiUrl);
      return;
    }

    const { out, enc } = await this.gzip(raw);
    if (out.byteLength === 0) {
      console.info('[cache] Skip put: compressed length is 0 for', apiUrl);
      return;
    }

    // Store metadata required for retrieval, size accounting, and eviction.
    const headers = new Headers();
    headers.set(HceDataCacheService.HDR_CREATED_AT, new Date().toISOString());
    headers.set(HceDataCacheService.HDR_CONTENT_LENGTH, String(out.byteLength));
    headers.set(HceDataCacheService.HDR_ENCODING, enc);
    // Use binary content type for gzip; otherwise plain UTF-8 text.
    headers.set('Content-Type', enc === 'gzip' ? 'application/octet-stream' : 'text/plain; charset=utf-8');
    // Keep the logical key alongside for listing and diagnostics.
    headers.set(HceDataCacheService.HDR_KEY, apiUrl);

    // Create a synthetic Response to insert into the Cache API.
    const response = new Response(out, { status: 200, headers });

    const request = this.makeRequest(apiUrl);
    await cache.put(request, response);

    // Enforce global quota; roll back this entry if eviction throws.
    try {
      await this.enforceQuota(cache);
    } catch (e) {
      await cache.delete(request);
      throw e;
    }
  }

  /** Retrieve text from Cache; returns null when no entry exists. */
  async get(apiUrl: string): Promise<string | null> {
    if (!this.ensureAvailable()) return null;

    const cache = await this.open();
    const req = this.makeRequest(apiUrl);
    const res = await cache.match(req);
    if (!res) return null;

    // Decompress if the stored encoding is gzip; otherwise read as-is.
    const enc = res.headers.get(HceDataCacheService.HDR_ENCODING) || 'identity';
    const known = new Set(['identity', 'gzip']);
    if (!known.has(enc)) {
      const msg = '[cache] Unknown encoding header; deleting cache and returning null';
      console.warn(msg, { apiUrl, enc });
      await cache.delete(req);
      return null;
    }

    const buf = await res.clone().arrayBuffer();
    const u8 = new Uint8Array(buf);
    const looksGzip = u8.byteLength >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;
    if ((enc === 'gzip' && !looksGzip) || (enc === 'identity' && looksGzip)) {
      const msg = '[cache] Encoding/header mismatch; deleting cache and returning null';
      console.warn(msg, { apiUrl, enc, looksGzip });
      await cache.delete(req);
      return null;
    }

    try {
      const bytes = await this.gunzip(buf, enc);
      return this.decode(bytes.buffer);
    } catch (e) {
      const msg = '[cache] Decompression/decoding failed; deleting cache and returning null';
      console.warn(msg, { apiUrl, enc, error: e });
      await cache.delete(req);
      return null;
    }
  }

  /** Delete a single entry by its API URL key. */
  async delete(apiUrl: string): Promise<boolean> {
    if (!this.ensureAvailable()) return false;
    return (await this.open()).delete(this.makeRequest(apiUrl));
  }

  /** Clear all entries in this named Cache. */
  async clear(): Promise<void> {
    if (!this.ensureAvailable()) return;
    const cache = await this.open();
    const keys = await cache.keys();
    await Promise.all(keys.map(r => cache.delete(r)));
  }

  /** List logical keys (X-Key if present; otherwise the request URL). */
  async keys(): Promise<string[]> {
    if (!this.ensureAvailable()) return [];
    const cache = await this.open();
    const reqs = await cache.keys();
    const list: string[] = [];
    for (const req of reqs) {
      const res = await cache.match(req);
      if (res) list.push(res.headers.get(HceDataCacheService.HDR_KEY) || req.url);
    }
    return list;
  }

  /** Compute the total stored size (after compression). */
  async getApproxSize(): Promise<number> {
    if (!this.ensureAvailable()) return 0;
    const cache = await this.open();
    let total = 0;
    for (const req of await cache.keys()) {
      const res = await cache.match(req);
      if (!res) continue;
      // Prefer the stored size header; fall back to actual body length if missing.
      const len = parseInt(res.headers.get(HceDataCacheService.HDR_CONTENT_LENGTH) || '', 10);
      total += Number.isFinite(len) ? len : (await res.clone().arrayBuffer()).byteLength;
    }
    return total;
  }

  /** Evict least-recent entries when total size exceeds 100MB. */
  private async enforceQuota(cache: Cache) {
    const reqs = await cache.keys();

    type Entry = { req: Request; createdAt: number; size: number };
    const entries: Entry[] = [];
    let total = 0;

    for (const r of reqs) {
      const res = await cache.match(r);
      if (!res) continue;

      // Parse creation time; NaN will sort as oldest later if not handled.
      const createdRaw = res.headers.get(HceDataCacheService.HDR_CREATED_AT) || '';
      const createdAt = Number.isFinite(Date.parse(createdRaw)) ? Date.parse(createdRaw) : 0;
      let size = parseInt(res.headers.get(HceDataCacheService.HDR_CONTENT_LENGTH) || '', 10);
      if (!Number.isFinite(size)) size = (await res.clone().arrayBuffer()).byteLength;

      entries.push({ req: r, createdAt, size });
      total += size;
    }

    // Reject a single item that exceeds the global limit on its own.
    if (entries.some(e => e.size > HceDataCacheService.MAX_BYTES)) {
      throw new Error('Item exceeds 100MB limit');
    }

    if (total <= HceDataCacheService.MAX_BYTES) return;

    // Evict in ascending creation time order (oldest first).
    entries.sort((a, b) => a.createdAt - b.createdAt);

    for (const e of entries) {
      if (total <= HceDataCacheService.MAX_BYTES) break;
      if (await cache.delete(e.req)) total -= e.size;
    }
  }
}
